import { useState } from "react";
import { supabase } from "../../supabaseClient";
import { Link } from "react-router-dom";
import "./VendorLogin.css"; // shared visual language with the login page
import "./VendorSignup.css"; // signup-specific field/layout additions

const FIELDS = [
  { key: "brand_name",     label: "Brand name",     type: "text",  placeholder: "Your business name", required: true },
  { key: "email",          label: "Email",           type: "email", placeholder: "you@business.com", required: true },
  { key: "contact_person", label: "Contact person",  type: "text",  placeholder: "Who should we reach out to?", required: true },
  { key: "phone_number",   label: "Phone number",    type: "text",  placeholder: "07XX XXX XXX", required: true },
  { key: "website",        label: "Website",         type: "text",  placeholder: "Optional", required: false },
  { key: "instagram",      label: "Instagram",       type: "text",  placeholder: "Optional", required: false },
  { key: "facebook",       label: "Facebook",        type: "text",  placeholder: "Optional", required: false },
];

const EMPTY_FORM = {
  brand_name: "",
  email: "",
  contact_person: "",
  phone_number: "",
  website: "",
  instagram: "",
  facebook: "",
};

export default function VendorSignup() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    const missing = FIELDS.filter((f) => f.required && !form[f.key].trim());
    if (missing.length > 0) {
      setErrorMsg(`Please fill in: ${missing.map((f) => f.label).join(", ")}`);
      return;
    }

    setSubmitting(true);
    const email = form.email.trim().toLowerCase();

    // Existing account already? No point requesting again.
    const { data: existingVendor } = await supabase
      .from("vendors")
      .select("id")
      .eq("email", email)
      .maybeSingle();

    if (existingVendor) {
      setSubmitting(false);
      setErrorMsg("An account with this email already exists — try logging in instead.");
      return;
    }

    const { data: existingRequest } = await supabase
      .from("vendor_signup_requests")
      .select("id, status")
      .eq("email", email)
      .maybeSingle();

    if (existingRequest) {
      setSubmitting(false);
      if (existingRequest.status === "pending") {
        setErrorMsg("You already have a request pending review — we'll email you once it's approved.");
      } else if (existingRequest.status === "approved") {
        setErrorMsg("Your request was already approved — check your email for login instructions.");
      } else {
        setErrorMsg("A previous request with this email was declined. Contact us if you'd like to try again.");
      }
      return;
    }

    const { error: insertErr } = await supabase
      .from("vendor_signup_requests")
      .insert({
        email,
        brand_name: form.brand_name.trim(),
        contact_person: form.contact_person.trim(),
        phone_number: form.phone_number.trim(),
        website: form.website.trim() || null,
        instagram: form.instagram.trim() || null,
        facebook: form.facebook.trim() || null,
        status: "pending",
      });

    setSubmitting(false);

    if (insertErr) {
      console.error("Signup request failed:", insertErr.message);
      setErrorMsg("Something went wrong submitting your request. Please try again.");
      return;
    }

    setSubmitted(true);
  };

  return (
    <div className="vendor-login-page">
      <div className="shape shape-blob-1" aria-hidden="true" />
      <div className="shape shape-blob-2" aria-hidden="true" />
      <div className="shape shape-ring" aria-hidden="true" />
      <div className="shape shape-dot-1" aria-hidden="true" />
      <div className="shape shape-dot-2" aria-hidden="true" />
      <div className="shape shape-triangle" aria-hidden="true" />

      <div className="login-card signup-card">
        <span className="eyebrow">Biz Baz Events</span>
        <h1>Request a vendor account</h1>

        {submitted ? (
          <p className="reset-sent-msg">
            Thanks — we've received your request. We'll review your details and
            email you once your account is approved and ready to log in.
          </p>
        ) : (
          <>
            <p className="signup-intro">
              Fill in your details below and we'll review your request. Once approved,
              you'll get an email to log in and complete your profile.
            </p>

            <form onSubmit={handleSubmit} noValidate>
              {errorMsg && (
                <p className="error" role="alert">
                  {errorMsg}
                </p>
              )}

              {FIELDS.map(({ key, label, type, placeholder, required }) => (
                <div key={key} className="field">
                  <label htmlFor={`signup-${key}`}>
                    {label}
                    {required && <span className="required-mark">*</span>}
                  </label>
                  <input
                    id={`signup-${key}`}
                    type={type}
                    value={form[key]}
                    onChange={(e) => set(key, e.target.value)}
                    placeholder={placeholder}
                    required={required}
                  />
                </div>
              ))}

              <button type="submit" className="submit-btn" disabled={submitting}>
                {submitting ? <span className="spinner" aria-hidden="true" /> : "Request account"}
              </button>
            </form>
          </>
        )}

        <p className="signup-prompt">
          Already have an account? <Link to="/vendor/login">Log in</Link>
        </p>
      </div>
    </div>
  );
}